import {BaseFormModel} from './base-form-model';
import {Order} from './order';
import {User} from './user';
import {Organization} from './organization';
import {Tax} from './tax';
import {PaymentCharge} from './payment-charge';
import {OrganizationRating} from './organization-rating';
import {Person} from './person';
import {CardSummary} from './card-summary';
import {OrderStatuses} from '../_constants/order-statuses';
import {TabPaymentInformation} from './tab-payment-information';
import {TabCredit} from './tab-credit';
import {CashAudit} from './cash-information/cash-audit';
import {CustomerAreaPosition} from './customer-area-position';
import {GiftCardAction} from './gift-card-action';
import {TabNote} from './tab-note';
import {addCurrency, subtractCurrency} from '../_utils/currency-math';
import {Deposit} from './deposit';
import {MembershipProgram} from './membership-program';
import {MembershipRenewal} from './membership-renewal';
import {Membership} from './membership';
import {DonationProgram} from './donation-program';

export class Tab extends BaseFormModel<Tab> {
    id: number;
    orgId: number;
    parentPickupLocationId: number;
    orders: Order[] = [];
    taxes: Tax[] = [];
    openCharges: PaymentCharge[];
    paymentCharges: PaymentCharge[];
    tabDeposits: Deposit[];
    cashAudits: CashAudit[];
    user: User;
    person: Person;
    server: Person;
    organization: Organization;
    startTime: Date;
    subtotal: number;
    creditsTotal: number;
    mobileFee: number;
    membershipFee: number;
    totalFee: number;
    deposits: number;
    invoiced: number;
    hostFee: number;
    organizationFee: number;
    status: string;
    pendingTip: number;
    rating: OrganizationRating;
    paymentInformation: TabPaymentInformation;
    credits: TabCredit[];
    giftCardPayments: GiftCardAction[];
    nameOverride: string;
    claimCode: string;
    deviceId: string;
    singleOrder: boolean;
    tabNumber: string;
    taxExemptionId: string;
    invoiceToken: string;
    customerAreaPosition: CustomerAreaPosition;
    potentialFee: number;
    tempToken: string;
    terminalId: string;
    fromTerminal: boolean;
    cardOnFile: CardSummary;
    notes: TabNote[];
    autoCloseFailed: boolean;
    organizationName: string;
    inFlight = false;
    membershipProgram: MembershipProgram;
    membershipRenewal: MembershipRenewal;
    member: Membership;
    key: string;
    version: number;
    preventAutoCredit: boolean;
    memberNumber: number;
    pickupLocationId: number;
    pickupLocationName: string;
    partySize: number;

    addedTaxes: number;
    donation: number;
    donationProgram: DonationProgram;

    // for userless
    cardSummary: CardSummary;
    timeZone: string;

    preload: boolean;
    fromCache = false;

    // transient
    due: number;
    surcharge: number;
    chargeDue: number;
    cashDue: number;
    tempExternalCaptureReferenceId: string;

    markForLogging = false;

    public constructor(jsonInput: any) {
        super(jsonInput);
        Object.assign(this, jsonInput);
        const orders = [];
        if (this.orders) {
            this.orders.forEach(o => {
                orders.push(new Order(o));
            });
        }
        this.orders = orders;

        const payments = [];
        if (this.paymentCharges) {
            this.paymentCharges.forEach(p => {
                payments.push(new PaymentCharge(p));
            });
        }
        this.paymentCharges = payments;

        const cashAudits = [];
        if (this.cashAudits) {
            this.cashAudits.forEach(c => {
                cashAudits.push(new CashAudit(c));
            });
        }
        this.cashAudits = cashAudits;
        this.customerAreaPosition = !!this.customerAreaPosition ? new CustomerAreaPosition(this.customerAreaPosition) : null;
    }

    public get personName(): string {
        if (!!this.nameOverride) {
            return this.nameOverride;
        } else {
            return !!this.person ? this.person.name : 'Terminal Order';
        }
    }

    sortOrders() {
        this.orders = this.orders.sort((a: Order, b: Order) => a.statusPriority() - b.statusPriority());
        const o = [];
        this.orders.forEach(i => {
            o.push(new Order(i));
        });
        this.orders = o;
    }

    activeOrders() {
        return this.orders.filter(o => o.status !== OrderStatuses.CANCELED
            && o.status !== OrderStatuses.VACATED
            && o.status !== OrderStatuses.DELETED);
    }

    init(): void {
        this.organization = this.organization ? new Organization(this.organization) : null;
        this.user = this.user ? new User(this.user) : null;
        this.membershipProgram = this.membershipProgram ? new MembershipProgram(this.membershipProgram) : null;
        this.membershipRenewal = this.membershipRenewal ? new MembershipRenewal(this.membershipRenewal) : null;
        this.member = this.member ? new Membership(this.member) : null;

        const temp = [];
        if (!!this.orders) {
            this.orders.forEach(r => {
                temp.push(new Order(r));
            });
            this.orders = temp;
        }
    }

    statusPriority() {
        let priority = 100;
        switch (this.status) {
        case 'OPEN':
            priority = 0;
            break;
        case 'CLOSED':
            priority = 10;
            break;
        }

        return priority;
    }

    get tipAgainstAmount() {
        const membershipRenewalsTotal = !!this.orders && this.orders.length > 0
            ? this.activeOrders().reduce((total, o) =>
                    addCurrency(total, !!o.items ? o.items.filter(i => !!i.membershipRenewal)
                        .reduce((itemTotal, i) => addCurrency(itemTotal, i.total), 0) : 0)
                , 0)
            : 0;

        let tipAgainstAmount = subtractCurrency(this.subtotal, membershipRenewalsTotal);

        const nonTokenPayments = subtractCurrency(subtractCurrency(this.paymentInformation.appliedPaymentsPrincipal,
            this.paymentInformation.tokenPaymentsTotal), this.paymentInformation.giftCardPaymentsPrincipal);

        if (nonTokenPayments > 0) {
            tipAgainstAmount = subtractCurrency(tipAgainstAmount, nonTokenPayments);
        }

        const includedMembershipFee = this.orders.reduce((membershipFee, order) => addCurrency(membershipFee,
            order.items.reduce((itemFee, i) => i.membershipFeeIncluded ? addCurrency(itemFee, i.membershipFees) : itemFee, 0)), 0);

        if (includedMembershipFee > 0) {
            tipAgainstAmount = subtractCurrency(tipAgainstAmount, includedMembershipFee);
        }

        if (tipAgainstAmount < 0) {
            tipAgainstAmount = 0;
        }

        return tipAgainstAmount;
    }
}
